import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Avatar} from '@spaced-out/ui-design-system/lib/components/Avatar';
import {
  BodyMedium,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {AnalyticsService} from 'src/analytics';
import {NOTIFICATIONS_EVENTS} from 'src/components/notifications/amplitude-events';
import {markNotificationAsRead} from 'src/action-creators/notifications';
import {interpolateNotificationBodyTemplate} from 'src/components/notifications/utils';
import {formateTimeAgo} from 'src/utils/date-time-2';
import moment from 'moment-timezone';
import {snake} from 'src/utils';
import {getTimezone} from 'src/selectors/date-time';
import type {NotificationType} from './types';
import css from './notification-item.css';


type NotificationItemProps = {
  notification: NotificationType;
  removeNotification: Boolean;
};

const NOTIFICATION_ICON = {
  system: 'bell',
  candidate_activity: 'user',
  error: 'circle-exclamation',
  referral: 'users',
  approval: 'file-signature',
  offer: 'file-contract',
  job_management: 'briefcase',
};

export const NotificationItem = ({
  notification,
  removeNotification,
}: NotificationItemProps) => {
  const dispatch = useDispatch();
  const {language, makeKey, t} = useLabelTranslation();
  const {resolvedBody, highlightValues} = interpolateNotificationBodyTemplate(
    notification.bodyTemplate,
    notification.context,
  );
  const timezone = useSelector(getTimezone) || '';
  const notificationTitle = moment(notification.createdAt)
    .tz(timezone)
    .locale(language)
    .format('Do MMMM YYYY, h:mm a');

  const handleNotificationClick = () => {
    AnalyticsService.track(NOTIFICATIONS_EVENTS.NOTIFICATION_CLICKED, {
      notificationId: notification.id,
      notificationType: notification.notificationType,
    });
    dispatch(markNotificationAsRead(notification.id, removeNotification));
    if (notification.deepLinkUrl) {
      window.open(notification.deepLinkUrl, '_blank');
    }
  };
  return (
    <div className={css.notification} onClick={handleNotificationClick}>
      <Avatar
        color="indigo"
        iconName={
          NOTIFICATION_ICON?.[
            notification.notificationType as keyof typeof NOTIFICATION_ICON
          ] || 'bell'
        }
        iconType="regular"
        size="medium"
        status={!notification.readAt ? 'information' : undefined}
      />
      <div className={css.notificationContent}>
        <BodyMedium
          highlightedTextClassName={css.highlightedText}
          highlightString={
            highlightValues.length > 0 ? highlightValues : undefined
          }
        >
          {notification.bodyTemplate &&
            t(makeKey(notification.bodyTemplate), {
              ...snake(notification.context || {}),
              defaultValue: resolvedBody,
            })}
        </BodyMedium>
        <div className={css.secondaryContent}>
          <Tooltip title={notificationTitle} placement="top" elevation="modal">
            <BodySmall color="secondary" className={css.timeAgo}>
              {formateTimeAgo(
                new Date(notification.createdAt).toISOString(),
                language,
              )}
            </BodySmall>
          </Tooltip>
          {notification.source && (
            <>
              <div className={css.sourceDot} />
              <BodySmall color="secondary" className={css.timeAgo}>
                {notification.source}
              </BodySmall>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
